import * as THREE from 'three';
import { ARENA_RADIUS } from '@moba/shared';

/** Isométrica clásica: giro de 45° y elevación de 35.26° (atan(1/√2)). */
export const ISO_YAW = THREE.MathUtils.degToRad(45);
export const ISO_PITCH = Math.atan(1 / Math.SQRT2);
/** Distancia de la cámara al foco: sólo afecta al recorte y a la niebla, no al tamaño. */
const CAMERA_DISTANCE = 45;
const MIN_SENSITIVITY = 0.25;
const MAX_SENSITIVITY = 3;

/**
 * Cámara ortográfica isométrica que sigue al jugador. El azul queda abajo a la
 * izquierda y el rojo arriba a la derecha, así la línea cruza la pantalla en
 * diagonal. La rueda del ratón cambia el zoom (alto visible en unidades).
 */
export class CameraRig {
  readonly camera: THREE.OrthographicCamera;

  /** mitad del alto visible, en unidades del mundo */
  viewHalfHeight = 11;
  shake = 0;
  /** false = cámara libre (Y): deja de seguir al héroe y se panea en los bordes */
  locked = true;
  /** multiplica el avance de la cámara hacia el cursor y la velocidad de paneo */
  mouseSensitivity = 1;

  private aspect: number;
  private readonly focus = new THREE.Vector3(0, 0, 0);
  private readonly offset: THREE.Vector3;
  private readonly desiredFocus = new THREE.Vector3();
  private readonly freeFocus = new THREE.Vector3(0, 0.9, 0);
  private readonly screenRight = new THREE.Vector3();
  private readonly screenUp = new THREE.Vector3();
  private initialized = false;

  constructor(aspect: number) {
    this.aspect = aspect;
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 400);
    // desde el lado azul (-X, -Z) mirando hacia el rojo
    this.offset = new THREE.Vector3(
      -Math.sin(ISO_YAW) * Math.cos(ISO_PITCH),
      Math.sin(ISO_PITCH),
      -Math.cos(ISO_YAW) * Math.cos(ISO_PITCH),
    ).multiplyScalar(CAMERA_DISTANCE);
    this.applyProjection();
  }

  resize(width: number, height: number): void {
    this.aspect = width / height;
    this.applyProjection();
  }

  zoom(deltaY: number): void {
    this.viewHalfHeight = THREE.MathUtils.clamp(this.viewHalfHeight + deltaY * 0.008, 7, 18);
    this.applyProjection();
  }

  addShake(amount: number): void {
    this.shake = Math.min(1.5, this.shake + amount);
  }

  setSensitivity(value: number): void {
    this.mouseSensitivity = THREE.MathUtils.clamp(value, MIN_SENSITIVITY, MAX_SENSITIVITY);
  }

  /** Bloquea (sigue al héroe) o libera la cámara. Al liberar arranca desde el foco actual. */
  setLocked(locked: boolean): void {
    if (this.locked === locked) return;
    this.locked = locked;
    if (!locked) this.freeFocus.set(this.focus.x, 0.9, this.focus.z);
  }

  /** Paneo en espacio de pantalla (positivo = derecha/arriba) con la cámara libre. */
  panScreen(px: number, py: number): void {
    if (this.locked) return;
    this.screenRight.setFromMatrixColumn(this.camera.matrixWorld, 0);
    this.screenUp.setFromMatrixColumn(this.camera.matrixWorld, 1);
    this.screenRight.y = 0;
    this.screenUp.y = 0;
    if (this.screenRight.lengthSq() < 1e-6 || this.screenUp.lengthSq() < 1e-6) return;
    this.screenRight.normalize();
    this.screenUp.normalize();
    this.freeFocus.x += this.screenRight.x * px + this.screenUp.x * py;
    this.freeFocus.z += this.screenRight.z * px + this.screenUp.z * py;
    const distance = Math.hypot(this.freeFocus.x, this.freeFocus.z);
    if (distance > ARENA_RADIUS) {
      this.freeFocus.x *= ARENA_RADIUS / distance;
      this.freeFocus.z *= ARENA_RADIUS / distance;
    }
  }

  update(dt: number, player: { x: number; z: number }, aim: { x: number; z: number }): void {
    if (this.locked) {
      const lead = 0.14 * this.mouseSensitivity;
      const maxLead = 2.4 * this.mouseSensitivity;
      const aimX = THREE.MathUtils.clamp((aim.x - player.x) * lead, -maxLead, maxLead);
      const aimZ = THREE.MathUtils.clamp((aim.z - player.z) * lead, -maxLead, maxLead);
      this.desiredFocus.set(player.x + aimX, 0.9, player.z + aimZ);
    } else {
      this.desiredFocus.copy(this.freeFocus);
    }

    if (!this.initialized) {
      this.focus.copy(this.desiredFocus);
      this.initialized = true;
    } else {
      this.focus.lerp(this.desiredFocus, 1 - Math.exp(-11 * dt));
    }

    this.camera.position.copy(this.focus).add(this.offset);
    if (this.shake > 0.001) {
      this.camera.position.x += (Math.random() * 2 - 1) * this.shake * 0.38;
      this.camera.position.y += (Math.random() * 2 - 1) * this.shake * 0.22;
      this.camera.position.z += (Math.random() * 2 - 1) * this.shake * 0.38;
      this.shake *= Math.exp(-7 * dt);
    } else {
      this.shake = 0;
    }
    this.camera.lookAt(this.focus);
  }

  private applyProjection(): void {
    const h = this.viewHalfHeight;
    this.camera.left = -h * this.aspect;
    this.camera.right = h * this.aspect;
    this.camera.top = h;
    this.camera.bottom = -h;
    this.camera.updateProjectionMatrix();
  }
}
